import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { getConfig } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';
import JobNavData from 'src/pages/jobs/container/jobNavData';
import LayoutCreateJob from 'src/pages/jobs/organisms/layout-create-job';

import { SplashScreen } from 'src/components/loading-screen/splash-screen';

import { AuthGuard } from 'src/auth/guard';
import { HRAuthGuard } from 'src/auth/guard/hr-guard';

// ----------------------------------------------------------------------

const JobsPage = lazy(() => import('src/pages/jobs/container/job'));
const JobEditor = lazy(() => import('src/pages/jobs/container/jobEditor'));
const CandidatesPage = lazy(() => import('src/pages/jobs/container/candidates/candidateMain'));
const CreateJobPage = lazy(() => import('src/pages/jobs/organisms/createJob'));
const JobWorkflows = lazy(() => import('src/pages/jobs/container/job-workflow/jobWorkflow'));
const InterviewHub = lazy(() => import('src/pages/jobs/organisms/interview-hub'));
const InterviewAgenda = lazy(() => import('src/pages/jobs/organisms/interview-agenda'));
const InterviewEvaluation = lazy(() => import('src/pages/jobs/organisms/interview-evaluation'));
const Compare = lazy(() => import('src/pages/jobs/organisms/compare'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  return (
    <Suspense fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const createJobLayout = () => (
  <LayoutCreateJob>
    <SuspenseOutlet />
  </LayoutCreateJob>
);

export function getJobsRoutes(): RouteObject[] {
  const CONFIG = getConfig();

  return [
  {
    path: 'jobs',
    element: CONFIG.auth.skip ? (
      dashboardLayout()
    ) : (
      <AuthGuard>
        <HRAuthGuard>{dashboardLayout()}</HRAuthGuard>
      </AuthGuard>
    ),
    children: [
      { element: <JobsPage />, index: true },
      {
        element: (
          <JobNavData>
            <Outlet />
          </JobNavData>
        ),
        children: [
          {
            path: 'job-details',
            element: <JobEditor />,
          },
          {
            path: 'candidates',
            element: <CandidatesPage />,
          },
          {
            path: 'workflow',
            element: <JobWorkflows />,
          },
          {
            path: 'interview-hub',
            element: <InterviewHub />,
          },
          {
            path: 'interview-agenda',
            element: <InterviewAgenda />,
          },
          {
            path: 'interview-evaluation',
            element: <InterviewEvaluation />,
          },
          {
            path: 'compare',
            element: <Compare />,
          },
        ],
      },
    ],
  },
  {
    path: 'jobs/create-job',
    element: CONFIG.auth.skip ? (
      createJobLayout()
    ) : (
      <AuthGuard>
        <HRAuthGuard>{createJobLayout()}</HRAuthGuard>
      </AuthGuard>
    ),
    children: [
      {
        element: <CreateJobPage />,
        index: true,
      },
    ],
    }
  ];
}
